import { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import {
  CalendarOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [{ key: "CUSTNAME", label: "Supplier Name" }];
const secondaryFields = [
  { key: "QTY", label: "Qty" },
  { key: "GSTAMT", label: "GST Amt" },
  { key: "NETAMT", label: "Net Amount" },
];

const DealerWisePurchase = () => {
  const [rawData, setRawData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ---- Fetch data directly from API: /api/ERP/GetDataFromGivenTableName?tableName=PURCHINVOICE_DATA ---- */
  useEffect(() => {
    setLoading(true);
    setTableVisible(false);

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=PURCHINVOICE_DATA`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = Array.isArray(response.data) ? response.data : [];
        setRawData(detailsData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching dealer wise purchase details:", error);
        setRawData([]);
        setTimeout(() => setTableVisible(true), 80);
      })
      .finally(() => setLoading(false));
  }, [tenantName]);

  /* Filter raw invoice data by selected date range */
  const dateFilteredData = useMemo(() => {
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    if (!dates || !dates[0] || !dates[1]) return rawData;

    const startDate = dates[0].startOf("day");
    const endDate = dates[1].endOf("day");

    return rawData.filter((item) => {
      const dateStr = item.INVDATE || item.ENTRYDATE;
      if (!dateStr) return true;
      const d = dayjs(dateStr);
      if (!d.isValid()) return true;
      return (
        (d.isAfter(startDate) || d.isSame(startDate, "day")) &&
        (d.isBefore(endDate) || d.isSame(endDate, "day"))
      );
    });
  }, [rawData, dates]);

  /* Group raw invoice line items by Dealer / Supplier (CustName / CUSTNAME / partyname) */
  const aggregatedData = useMemo(() => {
    if (!Array.isArray(dateFilteredData) || dateFilteredData.length === 0)
      return [];

    const map = new Map();

    dateFilteredData.forEach((item) => {
      const dealerName =
        item.CustName || item.CUSTNAME || item.partyname || "UNKNOWN";
      if (!map.has(dealerName)) {
        map.set(dealerName, {
          CUSTNAME: dealerName,
          QTY: 0,
          AMOUNT: 0,
          GSTAMT: 0,
          CGSTAMT: 0,
          SGSTAMT: 0,
          IGSTAMT: 0,
          NETAMT: 0,
        });
      }

      const entry = map.get(dealerName);
      entry.QTY += Number(item.qty ?? item.QTY ?? item.ITEM_QTY ?? 0);
      entry.AMOUNT += Number(item.Amount ?? item.amount ?? item.AMOUNT ?? 0);
      entry.GSTAMT += Number(item.GSTAMT ?? item.gstamt ?? item.VATAMT ?? 0);
      entry.CGSTAMT += Number(item.CGSTAMT ?? item.cgstamt ?? 0);
      entry.SGSTAMT += Number(item.SGSTAMT ?? item.sgstamt ?? 0);
      entry.IGSTAMT += Number(item.IGSTAMT ?? item.igstamt ?? 0);
      entry.NETAMT += Number(item.netamt ?? item.GAMOUNT ?? item.NETAMT ?? 0);
    });

    return Array.from(map.values()).map((row, index) => ({
      ...row,
      key: row.CUSTNAME || index + 1,
      serialNo: index + 1,
    }));
  }, [dateFilteredData]);

  /* Filter aggregated data by search text */
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return aggregatedData;
    const q = searchText.toLowerCase();
    return aggregatedData.filter((item) =>
      (item.CUSTNAME || "").toLowerCase().includes(q),
    );
  }, [aggregatedData, searchText]);

  /* Format data for table display */
  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        QTY: Number(item.QTY || 0),
        AMOUNT: Number(item.AMOUNT || 0),
        GSTAMT: Number(item.GSTAMT || 0),
        CGSTAMT: Number(item.CGSTAMT || 0),
        SGSTAMT: Number(item.SGSTAMT || 0),
        IGSTAMT: Number(item.IGSTAMT || 0),
        NETAMT: Number(item.NETAMT || 0),
      })),
    [filteredData],
  );

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Supplier Name",
      dataIndex: "CUSTNAME",
      key: "CUSTNAME",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "QTY",
      dataIndex: "QTY",
      key: "QTY",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#ef4444" }}>{Number(value || 0)}</strong>
      ),
    },
    {
      title: "Basic Amount",
      dataIndex: "AMOUNT",
      key: "AMOUNT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "GST AMT",
      dataIndex: "GSTAMT",
      key: "GSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "CGST AMT",
      dataIndex: "CGSTAMT",
      key: "CGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "SGST AMT",
      dataIndex: "SGSTAMT",
      key: "SGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "IGST AMT",
      dataIndex: "IGSTAMT",
      key: "IGSTAMT",
      align: "right",
      render: (value) => Number(value || 0).toFixed(2),
    },
    {
      title: "Net Amount",
      dataIndex: "NETAMT",
      key: "NETAMT",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  /* Calculate totals across formatted dataset */
  const totals = useMemo(() => {
    return formattedData.reduce(
      (acc, item) => {
        acc.qty += Number(item.QTY || 0);
        acc.amount += Number(item.AMOUNT || 0);
        acc.gst += Number(item.GSTAMT || 0);
        acc.cgst += Number(item.CGSTAMT || 0);
        acc.sgst += Number(item.SGSTAMT || 0);
        acc.igst += Number(item.IGSTAMT || 0);
        acc.net += Number(item.NETAMT || 0);
        return acc;
      },
      { qty: 0, amount: 0, gst: 0, cgst: 0, sgst: 0, igst: 0, net: 0 },
    );
  }, [formattedData]);

  const exportData = useMemo(
    () => [
      ...formattedData.map((item) => ({
        ...item,
        AMOUNT: item.AMOUNT.toFixed(2),
        GSTAMT: item.GSTAMT.toFixed(2),
        CGSTAMT: item.CGSTAMT.toFixed(2),
        SGSTAMT: item.SGSTAMT.toFixed(2),
        IGSTAMT: item.IGSTAMT.toFixed(2),
        NETAMT: item.NETAMT.toFixed(2),
      })),
      {
        serialNo: "Total",
        CUSTNAME: "",
        QTY: totals.qty,
        AMOUNT: totals.amount.toFixed(2),
        GSTAMT: totals.gst.toFixed(2),
        CGSTAMT: totals.cgst.toFixed(2),
        SGSTAMT: totals.sgst.toFixed(2),
        IGSTAMT: totals.igst.toFixed(2),
        NETAMT: totals.net.toFixed(2),
      },
    ],
    [formattedData, totals],
  );

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>
      <Header setOpen={setOpen} />
      <InventoryPageHeader
        title="Dealer Wise Purchase"
        subtitle="Purchase · Dealer Wise Purchase"
        icon={<UserOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={formattedData.length}
        visible={formattedData.length > 0}
        stats={[
          { label: "Suppliers", value: formattedData.length, color: "blue" },
          { label: "Qty", value: totals.qty, color: "amber" },
          { label: "GST Amt", value: totals.gst.toFixed(2), color: "purple" },
          {
            label: "Net Amount",
            value: totals.net.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>

          <FilterField icon={<SearchOutlined />} label="Search" theme="blue">
            <Input
              placeholder="Search supplier name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              bordered={false}
              allowClear
              style={{ width: 200 }}
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="DealerWisePurchase"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="key"
        tableClassName="dwp-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No purchase data found for the selected date range"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">{totals.qty}</Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.amount.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.gst.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.cgst.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.sgst.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.igst.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.net.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default DealerWisePurchase;
